import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, User } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTeamAgents } from "@/hooks/useTeamAgents";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logger } from "@/lib/logger";

interface EditNorthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  northId: string;
  currentAddress: string;
  currentCity: string;
  currentState: string;
  currentZip: string;
  currentSellerNames?: string;
  currentSellerEmail?: string;
  currentListingAgent?: string;
  currentListingAgentPhoto?: string;
  onSuccess?: () => void;
}

export const EditNorthDialog = ({
  open,
  onOpenChange,
  northId,
  currentAddress,
  currentCity,
  currentState,
  currentZip,
  currentSellerNames,
  currentSellerEmail,
  currentListingAgent,
  currentListingAgentPhoto,
  onSuccess,
}: EditNorthDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getEffectiveUserId } = useImpersonation();
  const { agents } = useTeamAgents();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [canSelectFromRoster, setCanSelectFromRoster] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [address, setAddress] = useState(currentAddress);
  const [city, setCity] = useState(currentCity);
  const [state, setState] = useState(currentState);
  const [zip, setZip] = useState(currentZip);
  const [sellerNames, setSellerNames] = useState(currentSellerNames || "");
  const [sellerEmail, setSellerEmail] = useState(currentSellerEmail || "");
  const [listingAgent, setListingAgent] = useState(currentListingAgent || "");
  const [listingAgentPhoto, setListingAgentPhoto] = useState(currentListingAgentPhoto || "");

  // Check admin status and match current agent to roster
  useEffect(() => {
    const checkAdminAndMatchAgent = async () => {
      if (open) {
        setAddress(currentAddress);
        setCity(currentCity);
        setState(currentState);
        setZip(currentZip);
        setSellerNames(currentSellerNames || "");
        setSellerEmail(currentSellerEmail || "");
        setListingAgent(currentListingAgent || "");
        setListingAgentPhoto(currentListingAgentPhoto || "");
        setSelectedAgentId("");

        try {
          const effectiveUserId = await getEffectiveUserId();
          if (!effectiveUserId) return;

          // Check if user is admin or agent (both can select from roster)
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', effectiveUserId)
            .in('role', ['admin', 'agent'])
            .maybeSingle();

          setCanSelectFromRoster(!!roleData);

          // If user has roster access and we have a current agent name, try to match it to roster
          if (roleData && currentListingAgent && agents.length > 0) {
            const matchedAgent = agents.find(
              a => a.name.toLowerCase().trim() === currentListingAgent.toLowerCase().trim()
            );
            if (matchedAgent) {
              setSelectedAgentId(matchedAgent.id);
              setListingAgent(matchedAgent.name);
              setListingAgentPhoto(matchedAgent.headshot_url || "");
            }
          }
        } catch (error) {
          logger.error('Error checking admin status or matching agent:', error);
        }
      }
    };

    checkAdminAndMatchAgent();
  }, [open, currentAddress, currentCity, currentState, currentZip, currentSellerNames, currentSellerEmail, currentListingAgent, currentListingAgentPhoto, agents, getEffectiveUserId]);

  // Handle agent selection from dropdown
  useEffect(() => {
    if (canSelectFromRoster) {
      if (selectedAgentId && selectedAgentId !== "none") {
        const selectedAgent = agents.find(a => a.id === selectedAgentId);
        if (selectedAgent) {
          setListingAgent(selectedAgent.name);
          setListingAgentPhoto(selectedAgent.headshot_url || "");
        }
      } else if (selectedAgentId === "none") {
        // Clear selection
        setListingAgent("");
        setListingAgentPhoto("");
      }
    }
  }, [selectedAgentId, agents, canSelectFromRoster]);

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${northId}-agent-headshot-${Date.now()}.${fileExt}`;
      const filePath = `agent-headshots/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("north-assets")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("north-assets")
        .getPublicUrl(filePath);

      setListingAgentPhoto(publicUrl);

      toast({
        title: "Success",
        description: "Agent photo uploaded successfully",
      });
    } catch (error) {
      logger.error("Error uploading photo:", error);
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!address.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      toast({
        title: "Error",
        description: "All address fields are required",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // If user selected from roster, use that agent's info
      let finalAgentName = listingAgent.trim() || null;
      let finalAgentHeadshot = listingAgentPhoto.trim() || null;

      if (canSelectFromRoster) {
        if (selectedAgentId && selectedAgentId !== "none") {
          const selectedAgent = agents.find(a => a.id === selectedAgentId);
          if (selectedAgent) {
            finalAgentName = selectedAgent.name;
            finalAgentHeadshot = selectedAgent.headshot_url;
          }
        } else {
          // User cleared selection or selected "none"
          finalAgentName = null;
          finalAgentHeadshot = null;
        }
      }

      const { data, error } = await supabase
        .from("norths")
        .update({
          address: address.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          seller_names: sellerNames.trim() || null,
          seller_email: sellerEmail.trim() || null,
          custom_agent_name: finalAgentName,
          custom_agent_headshot_url: finalAgentHeadshot,
        })
        .eq("id", northId)
        .select();

      if (error) {
        logger.error("EditNorthDialog: Update error:", error);
        throw error;
      }

      // Invalidate React Query cache to refresh Dashboard data
      // Invalidate the paginated dashboard query (this is what Dashboard.tsx uses)
      queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] });
      // Also invalidate the old query key for backward compatibility
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      
      // Also invalidate any north-specific queries
      queryClient.invalidateQueries({ queryKey: ['north', northId] });

      toast({
        title: "Success",
        description: "North details updated successfully",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error("EditNorthDialog: Error updating north:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error("EditNorthDialog: Error details:", {
        error,
        errorMessage,
        northId,
      });
      toast({
        title: "Error",
        description: `Failed to update north details: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit North Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="address">Street Address</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="San Francisco"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="CA"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="zip">ZIP Code</Label>
            <Input
              id="zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="94102"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sellerNames">Seller Names (Optional)</Label>
            <Input
              id="sellerNames"
              value={sellerNames}
              onChange={(e) => setSellerNames(e.target.value)}
              placeholder="John & Jane Doe"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sellerEmail">Seller Email (Optional)</Label>
            <Input
              id="sellerEmail"
              type="email"
              value={sellerEmail}
              onChange={(e) => setSellerEmail(e.target.value)}
              placeholder="seller@example.com"
            />
          </div>

          {canSelectFromRoster ? (
            <div className="space-y-2">
              <Label>Listing Agent</Label>
              <Select value={selectedAgentId || undefined} onValueChange={(value) => setSelectedAgentId(value === "none" ? "" : value)}>
                <SelectTrigger>
                  <SelectValue placeholder={agents.length > 0 ? "Select an agent from roster" : "No agents in roster yet"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Clear selection)</SelectItem>
                  {agents.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No agents in roster. Add agents in Settings → Team Roster
                    </div>
                  ) : (
                    agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={agent.headshot_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {agent.name[0]?.toUpperCase() || "A"}
                            </AvatarFallback>
                          </Avatar>
                          <span>{agent.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="listingAgent">Listing Agent Name (Optional)</Label>
                <Input
                  id="listingAgent"
                  value={listingAgent}
                  onChange={(e) => setListingAgent(e.target.value)}
                  placeholder="Agent Name"
                />
              </div>

              <div className="space-y-2">
                <Label>Listing Agent Photo (Optional)</Label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-full border-2 border-dashed border-border bg-muted flex items-center justify-center overflow-hidden">
                    {listingAgentPhoto ? (
                      <img
                        src={listingAgentPhoto}
                        alt="Agent headshot"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="agent-photo-upload"
                      disabled={isUploading}
                    />
                    <Label htmlFor="agent-photo-upload">
                      <Button variant="outline" size="sm" asChild disabled={isUploading}>
                        <span className="cursor-pointer">
                          {isUploading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Photo
                            </>
                          )}
                        </span>
                      </Button>
                    </Label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
